import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { fmtMoney, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { ArrowDownLeft, ArrowUpRight, ShieldCheck, Volume2, VolumeX, TrendingUp, Eye, EyeOff, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { announcePayment, unlockAudio } from "@/lib/sound";

const walletMeta = [
  { key: "collection", label: "Collection Wallet", grad: "from-blue-600 to-indigo-700", icon: ArrowDownLeft },
  { key: "payout", label: "Payout Wallet", grad: "from-emerald-600 to-teal-700", icon: ArrowUpRight },
  { key: "chargeback", label: "Chargeback Wallet", grad: "from-amber-500 to-orange-600", icon: ShieldCheck },
];

function StatusBadge({ s }) {
  const map = {
    success: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    rejected: "bg-rose-500/10 text-rose-600 border-rose-500/20",
  };
  return <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium capitalize ${map[s] || "bg-slate-500/10 text-slate-500 border-slate-500/20"}`}>{s}</span>;
}

export default function UserDashboard() {
  const [data, setData] = useState(null);
  const [mask, setMask] = useState(false);
  const [sound, setSound] = useState(true);
  const soundRef = useRef(true);
  const seenRef = useRef(null); // last seen payin count
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/user/dashboard");
      setData(data);
      if (firstLoad.current) {
        seenRef.current = data.payin_count;
        firstLoad.current = false;
      } else if (data.payin_count > seenRef.current) {
        const diff = data.payin_count - seenRef.current;
        seenRef.current = data.payin_count;
        if (soundRef.current) announcePayment(data.last_payin_amount);
        toast.success(`Payment received • ${fmtMoney(data.last_payin_amount)}`, { description: diff > 1 ? `${diff} new credits` : "Added to collection wallet" });
      }
    } catch (e) {
      /* silent on poll */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { soundRef.current = sound; }, [sound]);

  if (!data) return <div className="py-20 text-center text-slate-400 animate-pulse">Loading dashboard…</div>;

  const wallets = data.wallets;
  const series = data.series || [];

  return (
    <div className="space-y-6" data-testid="user-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Dashboard</h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
            <span className="h-2 w-2 rounded-full bg-emerald-500 pulse-dot" /> Live • auto-refreshing every 5s
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMask((m) => !m)} data-testid="toggle-balance-mask" className="rounded-full">
            {mask ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { unlockAudio(); setSound((s) => !s); toast.info(sound ? "Sound muted" : "Sound on"); }} data-testid="toggle-sound-btn" className="rounded-full">
            {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="manual-refresh-btn" className="rounded-full"><RotateCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Wallets */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {walletMeta.map((w, i) => (
          <motion.div key={w.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${w.grad} text-white p-5 shadow-lg`} data-testid={`${w.key}-wallet-card`}>
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/90">{w.label}</span>
              <w.icon className="h-5 w-5 text-white/80" />
            </div>
            <div className="mt-6 font-mono-data text-2xl sm:text-3xl font-bold tracking-tight" data-testid={`${w.key}-wallet-balance`}>
              {mask ? "••••••" : fmtMoney(wallets[w.key])}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Totals */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { label: "Total Pay-In", val: data.totals.payin, icon: ArrowDownLeft, color: "text-blue-600" },
          { label: "Total Payout", val: data.totals.payout, icon: ArrowUpRight, color: "text-emerald-600" },
          { label: "Pending Payout", val: data.totals.pending_payout, icon: TrendingUp, color: "text-amber-600" },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{s.label}</span>
              <s.icon className={`h-4 w-4 ${s.color}`} />
            </div>
            <div className="mt-2 font-mono-data text-xl font-bold text-slate-900 dark:text-white">{mask ? "••••" : fmtMoney(s.val)}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">Pay-In — last 7 days</h3>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="gpayin" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F62FE" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0F62FE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.3} />
                <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" width={40} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Area type="monotone" dataKey="payin" stroke="#0F62FE" strokeWidth={2} fill="url(#gpayin)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">Payout — last 7 days</h3>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.3} />
                <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" width={40} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="payout" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">Recent activity</h3>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.recent.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No transactions yet</div>}
          {data.recent.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`txn-row-${t.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${t.type === "payin" ? "bg-blue-500/10 text-blue-600" : t.type === "payout" ? "bg-emerald-500/10 text-emerald-600" : "bg-slate-500/10 text-slate-600"}`}>
                  {t.type === "payin" ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium capitalize text-slate-900 dark:text-white">{t.type}{t.account_name ? ` • ${t.account_name}` : ""}</div>
                  <div className="text-xs text-slate-500 font-mono-data">{t.utr ? `UTR ${t.utr}` : new Date(t.created_at).toLocaleString("en-IN")}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={`font-mono-data text-sm font-semibold ${t.type === "payin" ? "text-emerald-600" : "text-slate-900 dark:text-white"}`}>{t.type === "payin" ? "+" : "-"}{fmtMoney(t.amount)}</div>
                <StatusBadge s={t.status} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
